import moment from 'moment';

import { withRouter } from 'react-router-dom';
import { MoonLoader } from 'react-spinners';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';

import WorkObjectApi from 'api/work-object';
import WorkObjectsHeader from 'components/bots/workobjects/WorkObjectsHeader';
import DataGridWithPagination from 'components/ui/DataGrid/DataGridWithPagination';

import { Status } from 'helpers/StatusHelper';

const WorkObjectsPage = ({ match }) => {
  const { t } = useTranslation();

  const [state, setState] = useState({
    fetching: true,
    data: {},
    page: 1,
    pageSize: 50,
    order: [['created_at', 'asc']],
    sortColumns: [],
  });

  useEffect(() => {
    fetchData();
  }, [state.page, state.search, state.order, state.pageSize]);

  const fetchData = async () => {
    setState(prevState => {
      return { ...prevState, fetching: true };
    });

    try {
      const { page, pageSize, search, order } = state;
      const data = await WorkObjectApi.allByStatus({ ...match.params }, { page, search, pageSize, order });
      setState(prevState => {
        return { ...prevState, data };
      });
    } finally {
      setState(prevState => {
        return { ...prevState, fetching: false };
      });
    }
  };

  const dataFormatter = ({ column, row }) => {
    const date_value = row[column.key];

    if (!date_value) {
      return '--';
    }

    return moment(date_value).format('DD/MM/YYYY HH:mm');
  };

  const errorFormatter = ({ column, row }) => {
    const date_value = row[column.key];
    return <div title={date_value}>{date_value}</div>;
  };

  const statusFormatter = ({ row }) => {
    const status_key = (row._status || '').toUpperCase();
    const status = Status[status_key];
    const status_desc = t(status.tkey).toUpperCase();

    switch (row._status) {
      case 'FAILED':
        return <div className="text-danger">{status_desc}</div>;

      case 'PROCESSING':
        return (
          <div className="text-primary d-flex align-work_objects-center justify-content-between">
            <MoonLoader size={16} color="#7930e2" />
            <div className="ms-3">{status_desc}</div>
          </div>
        );

      case 'DONE':
        return <div className="text-success">{status_desc}</div>;

      case 'NOT_DONE':
        return <div className="text-warning">{status_desc}</div>;

      default:
        return <div className="text-muted">{status_desc}</div>;
    }
  };

  const { data } = state;

  const job = data.job || {};
  const bot = job.bot || {};
  const work_objects_data = data.work_objects || {};
  const work_objects = work_objects_data.rows || [];
  const work_objects_count = work_objects_data.count || work_objects.length;

  const work_object_config = (bot.schemas || []).find(item => item.id === 'work_object') || {};
  const schema = work_object_config.schema || {};

  const properties = schema.properties || {};

  const defaultColumnProperties = {
    resizable: true,
  };

  const dynamic_columns = [];
  const fixed_columns = [
    { key: '_status', name: 'Status', width: 'auto', formatter: statusFormatter },
    { key: '_attempts', name: t('columns.attempts'), width: 'auto' },
    { key: '_createdAt', name: t('columns.created_at'), width: 'auto', formatter: dataFormatter },
    { key: '_started_at', name: t('columns.started_at'), width: 'auto', formatter: dataFormatter },
    { key: '_done_at', name: t('columns.done_at'), width: 'auto', formatter: dataFormatter },
  ];

  if (['failed', 'not_done'].includes(match.params.status.toLowerCase())) {
    fixed_columns.push({ key: '_error', name: t('columns.reason'), width: 300, resizable: true, formatter: errorFormatter });
  }

  fixed_columns.push({ key: '_empty', name: '', width: 'auto', resizable: true });

  for (const key in properties) {
    dynamic_columns.push({ key, name: properties[key].title || key, sortable: true });
  }

  const columns = [...dynamic_columns, ...fixed_columns].map(c => ({ ...c, ...defaultColumnProperties }));

  const rows = work_objects.map(item => {
    const errors = item.errors || [];
    const first_error = errors[0] || {};
    const payload = first_error.payload || {};
    const error = payload.error || {};

    return {
      ...item.payload,
      _status: item.status,
      _attempts: item.attempts,
      _createdAt: item.createdAt,
      _done_at: item.done_at,
      _started_at: item.started_at,
      _start_at: item.start_at,
      _error: JSON.stringify(error.message || 'Desconhecido'),
    };
  });

  const handlePageChange = page => {
    setState(prevState => {
      return { ...prevState, page };
    });
  };

  const onSearch = search => {
    setState(prevState => {
      return { ...prevState, search, page: 1 };
    });
  };

  const onSortColumnsChange = ([sortColumn = {}]) => {
    const { columnKey, direction } = sortColumn;

    if (!columnKey && !direction) {
      setState(prevState => {
        return { ...prevState, order: [['created_at', 'asc']], page: 1, sortColumns: [] };
      });
      return;
    }

    const order = [[`payload.${columnKey}`, direction.toLowerCase()]];

    setState(prevState => {
      return { ...prevState, order, page: 1, sortColumns: [sortColumn] };
    });
  };

  const onChangePageSize = ({ value }) => {
    setState(prevState => {
      return { ...prevState, page: 1, pageSize: value };
    });
  };

  const { page: current_page, pageSize, sortColumns } = state;
  return (
    <>
      <WorkObjectsHeader {...data} onSearch={onSearch} />

      <div className="container mt-0">
        <DataGridWithPagination
          grid={{ columns, rows }}
          pagination={{ current_page, count: work_objects_count, onPageChange: handlePageChange, pageSize }}
          loading={state.fetching}
          sortColumns={sortColumns}
          onSortColumnsChange={onSortColumnsChange}
          onChangePageSize={onChangePageSize}
        />
      </div>
    </>
  );
};

export default withRouter(WorkObjectsPage);
